import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

const ProductCard = ({ product, navigationContext, showPrices = true, lowStockThreshold = 30 }) => {
  const [hovered, setHovered] = useState(false);

  const images = product.images || [];
  const mainImage = images[product.main_image_index] || images[0];
  const secondImage = images.find((_, idx) => idx !== (product.main_image_index || 0)) || mainImage;
  const previewImage = hovered && secondImage ? secondImage : mainImage;

  const stockNum = Number(product.stock || 0);
  const isLowStock = stockNum > 0 && stockNum <= lowStockThreshold;

  const buildProductUrl = () => {
    let url = `/product/${product.id}`;
    if (navigationContext?.category) {
      const params = new URLSearchParams();
      params.set('from', navigationContext.category);
      if (navigationContext.categoryName) {
        params.set('categoryName', navigationContext.categoryName);
      }
      url += `?${params.toString()}`;
    }
    return url;
  };

  return (
    <Link
      to={buildProductUrl()}
      data-testid={`product-card-${product.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`group relative block bg-[#0A0A0A] border transition-all duration-500 hover:-translate-y-1 ${
        isLowStock
          ? 'border-red-500/50 hover:border-red-400/80 shadow-[0_0_24px_-6px_rgba(239,68,68,0.35)]'
          : 'border-white/[0.06] hover:border-white/25'
      }`}
    >
      {/* Image well — deep, elegant background */}
      <div className="relative aspect-[4/5] overflow-hidden bg-gradient-to-b from-[#141414] via-[#101010] to-[#0A0A0A]">
        {/* Subtle radial highlight */}
        <div
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at top, rgba(255,255,255,0.04), transparent 60%)',
          }}
        />

        {/* Image */}
        {previewImage && (
          <img
            src={previewImage}
            alt={product.name}
            className="absolute inset-0 w-full h-full object-contain p-5 sm:p-8 transition-all duration-[1200ms] ease-[cubic-bezier(0.25,1,0.5,1)] group-hover:scale-[1.04]"
            loading="lazy"
          />
        )}

        {/* Top-left badge — minimal */}
        {product.featured && (
          <div
            data-testid="badge-featured"
            className="absolute top-3 left-3 sm:top-4 sm:left-4 z-10 bg-white text-black text-[9px] sm:text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 font-semibold"
          >
            Nuevo
          </div>
        )}

        {/* Top-right sale badge */}
        {product.promotional_price && showPrices && (
          <div
            data-testid="badge-sale"
            className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 bg-black/60 backdrop-blur-sm border border-white/30 text-white text-[9px] sm:text-[10px] uppercase tracking-[0.25em] px-2.5 py-1"
          >
            Sale
          </div>
        )}

        {/* Low stock badge — "Última curva" */}
        {isLowStock && (
          <div
            data-testid="badge-low-stock"
            className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 z-10 bg-red-500/15 backdrop-blur-sm border border-red-400/60 text-red-300 text-[9px] sm:text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 font-semibold animate-pulse"
          >
            Última curva
          </div>
        )}

        {/* Hover overlay — quick view */}
        <div className="absolute inset-x-0 bottom-0 z-10 pointer-events-none">
          <div className="bg-gradient-to-t from-black/70 via-black/20 to-transparent p-3 sm:p-4 translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
            <div className="flex items-center justify-between gap-2 text-white">
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.25em] font-semibold">Ver Detalle</span>
              <ArrowUpRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-white/[0.06] group-hover:bg-white/15 transition-colors duration-500" />

      {/* Info */}
      <div className="px-3 py-4 sm:px-4 sm:py-5">
        <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1.5 sm:mb-2 truncate">
          {product.brand}
        </p>
        <h3 className="text-xs sm:text-sm font-light text-white mb-2 sm:mb-3 line-clamp-2 leading-snug min-h-[2.5rem] sm:min-h-[2.75rem]">
          {product.name}
        </h3>

        {showPrices ? (
          <div className="flex items-baseline gap-2 sm:gap-3">
            {product.promotional_price ? (
              <>
                <p className="text-sm sm:text-base font-semibold text-white tabular-nums">
                  ${product.promotional_price.toLocaleString()}
                </p>
                <p className="text-[10px] sm:text-xs text-white/35 line-through tabular-nums">
                  ${product.price.toLocaleString()}
                </p>
              </>
            ) : (
              <p className="text-sm sm:text-base font-semibold text-white tabular-nums">
                ${product.price.toLocaleString()}
              </p>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/50">
              Consultar precio
            </p>
            <ArrowUpRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white/30 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
          </div>
        )}
      </div>
    </Link>
  );
};

export default ProductCard;
